import React, { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useCombobox, UseComboboxProps } from 'downshift';
import { colors } from '@styleseat/brand';
import { Box } from '@styleseat/ui';
import Label from './Label';
import Message from './Message';
import { Stack, TextFormFieldProps } from '../index';
import TextInput from '../TextInput/TextInput';

import './AutocompleteFormField.module.scss';

type AutocompleteFormFieldProps = TextFormFieldProps & {
  handleSelectedItemChange: UseComboboxProps<React.ReactNode>['onSelectedItemChange'];
  Item: React.FC<any>,
  items: React.ReactNode[];
};

/**
 * A Text Autocomplete Input Form Component implementation atop `TextInput`.
 */
const AutocompleteFormField: React.FC<AutocompleteFormFieldProps> = ({
  message,
  label,
  accentColor,
  Item = ({ children }) => children,
  items,
  handleSelectedItemChange,
  testID,
  ...restProps
}) => {
  const id = useMemo(() => `autocomplete-form-field-${uuidv4()}`, []);

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items,
    onSelectedItemChange: handleSelectedItemChange,
  });

  const moreInputProps = getInputProps();

  return (
    <Box>
      <Stack>
        <Label label={label} testID={testID} id={id} />
        <Box>
          <div {...getComboboxProps()}>
            <TextInput
              {...moreInputProps}
              {...restProps}
              appearance="form"
              id={id}
              testID={testID}
            />
          </div>
          <Box testID={`${testID}-menu`}>
            <div {...getMenuProps()} styleName={`menu ${items.length && isOpen ? 'open' : ''}`}>
              {isOpen && (
                items.map((item, index) => {
                  const key = (item as React.ReactElement)?.key || item;
                  const rawItemID = typeof item === 'string'
                    ? item
                    : (
                      (item as React.ReactElement)?.key
                      || index
                    )?.toString();
                  const itemID = rawItemID?.replace(/\W/g, '').toLowerCase();
                  return (
                    <div
                      data-testid={testID && `${testID}-${itemID}`}
                      style={
                          highlightedIndex === index ? { backgroundColor: colors.chill } : {}
                        }
                      key={key}
                      {...getItemProps({ item, index })}
                    >
                      <Item>
                        {item}
                      </Item>
                    </div>
                  );
                })
              )}
            </div>
          </Box>
        </Box>
        <Message message={message} accentColor={accentColor} testID={testID} />
      </Stack>
    </Box>
  );
};

export default AutocompleteFormField;
